import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Warehouse, CheckCircle, ArrowRight } from 'lucide-react';

const WMS = () => {
  const features = [
    'Real-time inventory tracking and management',
    'Automated warehouse operations',
    'Order management and fulfillment',
    'Receiving and putaway optimization',
    'Picking and packing automation',
    'Shipping and logistics integration',
    'Barcode and RFID support',
    'Multi-warehouse management',
    'Advanced reporting and analytics',
    'Mobile device support'
  ];

  const benefits = [
    {
      title: 'Increase Efficiency',
      description: 'Streamline warehouse operations and reduce manual tasks by up to 60%'
    },
    {
      title: 'Reduce Costs',
      description: 'Optimize space utilization and reduce labor costs significantly'
    },
    {
      title: 'Improve Accuracy',
      description: 'Achieve 99.9% inventory accuracy with automated tracking'
    },
    {
      title: 'Real-time Visibility',
      description: 'Get complete visibility into warehouse operations 24/7'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white py-16 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="flex items-center justify-center mb-6">
            <Warehouse className="w-16 h-16 text-blue-400" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center">Warehouse Management System</h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto text-center">
            Complete warehouse automation and management solution for modern supply chains
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">Transform Your Warehouse Operations</h2>
            <p className="text-lg text-slate-600">
              Our Warehouse Management System (WMS) is a comprehensive solution designed to optimize every aspect of your warehouse operations. From receiving to shipping, our system provides complete visibility and control over your inventory and processes.
            </p>
          </div>

          {/* Features */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-8 text-center">Key Features</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start space-x-3 bg-white p-4 rounded-lg shadow-sm">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Benefits */}
          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-8 text-center">Benefits</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <CardHeader>
                    <CardTitle className="text-xl">{benefit.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-600">{benefit.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-gradient-to-br from-blue-600 to-cyan-600 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Optimize Your Warehouse?</h2>
          <p className="text-xl mb-8 text-blue-100">
            Contact us today to learn how our WMS can transform your operations
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-slate-100 px-8 py-6 text-lg transition-all duration-300 hover:scale-105">
              Request a Demo <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default WMS;